import {
  IconButton,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogProps as MuiDialogProps,
  DialogContentProps,
  DialogActionsProps as MuiDialogActionProps,
  useMediaQuery,
} from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useFocusStyles } from "hooks/focusBorder";
import { useViewport } from "hooks/useViewport";
import { useTranslation } from "react-i18next";

// Custom props that should not be forwarded to DOM
const customProps = [
  "isMobile",
  "max_Width",
  "maxHeight",
  "minHeight",
  "minWidth",
  "disableCloseIcon",
  "isLargeHeight",
  "alignContent",
];
const shouldForwardProp = (prop: string) => !customProps.includes(prop);

type StyledDialogProps = {
  isMobile?: boolean;
  max_Width?: number | string;
  minWidth?: number | string;
  maxHeight?: number | string;
  minHeight?: number | string;
  isLargeHeight?: boolean;
};

const StyledDialog = styled(MuiDialog, { shouldForwardProp })<StyledDialogProps>(
  ({ theme, minHeight, isLargeHeight, isMobile, maxHeight, max_Width, minWidth }) => ({
    "& .MuiDialog-container": {
      minHeight: minHeight ? "360px" : isLargeHeight ? "652px" : "90vh",
      maxHeight: isMobile ? "100%" : maxHeight ? maxHeight : "90vh",
      maxWidth: max_Width ? max_Width : 720,
      minWidth: minWidth ? minWidth : 200,
      padding: isMobile ? "0" : theme.spacing(3),
      margin: "auto",
    },
    [theme.breakpoints.up("xs")]: {
      "& .MuiDialog-paperWidthXs": {
        maxWidth: "335px",
      },
    },
    [theme.breakpoints.up("sm")]: {
      "& .MuiDialog-paperWidthSm": {
        maxWidth: max_Width ? max_Width : "660px",
        borderRadius: "12px",
      },
    },
    "& .MuiDialog-paper": {
      minHeight: minHeight ? minHeight : "",
      width: minWidth ? minWidth : "",
    },
  }),
);

export const DialogTitle = styled(MuiDialogTitle)({
  display: "flex",
  justifyContent: "flex-end",
  padding: 0,
});

type StyledDialogContentProps = DialogContentProps & { isMobile?: boolean };
export const DialogContent = styled(MuiDialogContent, {
  shouldForwardProp,
})<StyledDialogContentProps>(({ isMobile }) => ({
  overflow: isMobile ? undefined : "hidden",
}));

type DialogActionsProps = MuiDialogActionProps & { isMobile?: boolean; alignContent?: string };
export const DialogActions = styled(MuiDialogActions, { shouldForwardProp })<DialogActionsProps>(
  ({ isMobile, alignContent }) => ({
    display: "flex",
    justifyContent: alignContent ? alignContent : "flex-end",
    padding: isMobile ? "0px 0px 0px 0px" : "20px 25px 30px 25px",
  }),
);

export const keyDownHandler = (dialogtitle: string, e): void => {
  // only execute if tab is pressed
  if (e.key !== "Tab") return;

  const modalRef = document.getElementById(dialogtitle);

  // here we query all focusable elements, customize as your own need
  const focusableModalElements = modalRef.querySelectorAll(
    "a[href], button:not([disabled]), textarea, input, select",
  );

  const firstElement = focusableModalElements[0];
  const lastElement = focusableModalElements[focusableModalElements.length - 1];

  // if going forward by pressing tab and lastElement is active shift focus to first focusable element
  if (!e.shiftKey && document.activeElement === lastElement) {
    (firstElement as HTMLElement)?.focus();
    return e.preventDefault();
  }

  // if going backward by pressing tab and firstElement is active shift focus to last focusable element
  if (e.shiftKey && document.activeElement === firstElement) {
    (lastElement as HTMLElement)?.focus();
    e.preventDefault();
  }
};

type DialogProps = MuiDialogProps & {
  isMobile?: boolean;
  max_Width?: number | string;
  minWidth?: number | string;
  maxHeight?: number | string;
  minHeight?: number | string;
  disableCloseIcon?: boolean;
  isLargeHeight?: boolean;
  role?: string;
  id: string;
};
export const Dialog = ({
  children,
  onClose,
  showCloseIcon,
  id,
  // Destructure custom props for use in styling
  isMobile: propIsMobile,
  maxHeight,
  minHeight,
  max_Width,
  minWidth,
  disableCloseIcon,
  isLargeHeight: propIsLargeHeight,
  ...props
}: DialogProps & {
  onClose: React.MouseEventHandler<HTMLButtonElement> | undefined;
  showCloseIcon?: boolean;
}): React.ReactElement => {
  const isLargeHeight = propIsLargeHeight ?? useMediaQuery("(min-height:700px)");
  const { isMobile: viewportIsMobile } = useViewport();
  const isMobile = propIsMobile ?? viewportIsMobile;
  const focusClass = useFocusStyles();
  const { t } = useTranslation();

  // Handle backdrop click prevention
  const handleClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") {
      return; // Prevent close on backdrop click
    }
    if (onClose) {
      onClose(event as any);
    }
  };

  return (
    <StyledDialog
      fullScreen={isMobile ? true : false}
      id={id}
      onKeyDown={(e) => {
        keyDownHandler(id, e);
      }}
      {...props}
      onClose={handleClose}
      disableEnforceFocus={true}
      scroll={isMobile ? "body" : "paper"}
      // Pass styling props (shouldForwardProp prevents DOM forwarding)
      isMobile={isMobile}
      maxHeight={maxHeight}
      minHeight={minHeight}
      max_Width={max_Width}
      minWidth={minWidth}
      isLargeHeight={isLargeHeight}
    >
      {showCloseIcon && !disableCloseIcon && (
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            aria-label={t("close").toLowerCase() + " " + props.title}
            className={focusClass.focusItem}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}
      {children}
    </StyledDialog>
  );
};
